import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSnapshot } from 'valtio';
import { InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Tooltip, message } from 'antd';
import { useLocation, useNavigate } from 'react-router';
import SuperButton from './SuperButton';
import { visitor } from '@/utils/store/visitor';
import { supabaseAuthStore } from '@/utils/store/supabaseAuth';
import { reportPV } from '@/services/api';

/**
 * 侧边栏底部组件，包含用户信息和登录状态
 */
const SidebarFoot: React.FC = () => {
    // 只使用 Supabase 认证状态
    const supabaseAuth = useSnapshot(supabaseAuthStore);
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const isDebug = query.get("isDebug");

    // 不会出现登录时，isLoading为false，isAuthenticated没有加载，同时根据user===undefined二次保险
    const isNotLogin = !supabaseAuth.isLoading && !supabaseAuth.isAuthenticated && supabaseAuth.user === undefined;

    // 允许访问评测页面，即使未登录
    const allowedPaths = [
        "/", "/evaluation", "/agreement"
    ];
    const isAllowedPath = allowedPaths.some(path =>
        location.pathname === path
        || location.pathname.startsWith(path + "/")
    );

    if (!isAllowedPath && isNotLogin) {
        window.location.href = "/";
    }

    // useEffect(() => {
    //   if (isAuthenticated) {
    //     runFetchVIP(user?.sub);
    //   }
    // }, [isAuthenticated, user?.sub, runFetchVIP]);

    // useEffect(() => {
    //   // VIP数据变化时的处理逻辑
    // }, [vipdata]);


    // 上报PV信息
    useEffect(() => {
        visitor.getFingerprint().then(() => {
            if(supabaseAuth.isLoading){
                return;
            }
            
            const vid = visitor.visitorId;
            const isGuest = !supabaseAuth.isAuthenticated;
            reportPV(
                {
                    userId: supabaseAuth.id || undefined,
                    guestId: vid,
                    isGuest
            }
            );
        });
    }, [supabaseAuth.isLoading, supabaseAuth]);

  return (
    <div className="flex-none pt-2 pb-3 px-3 border-t border-[#E9E9E7]">
      {isDebug && isDebug === "true" && (
        <button onClick={() => console.log("debug Info: ", supabaseAuth.user)} className="text-xs text-gray-400 mb-1">
          debug
        </button>
      )}

      <div className="space-y-1">
        {/* 我的 / 用户信息 */}
        {supabaseAuth.isAuthenticated && (
          <button
            onClick={() => navigate("/user/detail")}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-[#37352F] hover:bg-[#EFEFED] transition-colors duration-200 cursor-pointer"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            我的
            {supabaseAuth.isAuthenticated && <SuperButton />}
          </button>
        )}

        {/* 登录/登出按钮 */}
        <button
          onClick={() => {
            if (supabaseAuth.isLoading) {
              message.info("正在加载");
              return;
            }
            if (supabaseAuth.isAuthenticated) {
              window.location.href = '/s_auth/logout';
            } else {
              window.location.href = '/s_auth/login';
            }
          }}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 cursor-pointer ${
            supabaseAuth.isAuthenticated
              ? 'text-red-500 hover:bg-red-50'
              : 'text-[#37352F] hover:bg-[#EFEFED]'
          }`}
        >
          {supabaseAuth.isLoading ? (
            <div className="loading loading-spinner loading-xs" />
          ) : supabaseAuth.isAuthenticated ? (
            <>
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              登出
            </>
          ) : (
            <>
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
              登录 / 注册
            </>
          )}
        </button>

        {!supabaseAuth.isAuthenticated && (
          <Tooltip title="当前为游客用户，试用次数不足时需要登录后继续使用">
            <p className="text-[10px] text-[#91918E] px-3 py-1">游客模式</p>
          </Tooltip>
        )}
      </div>

      {/* 底部链接 */}
      <div className="text-[9px] text-[#91918E] pt-2 flex justify-center gap-2 flex-wrap">
        <span className="cursor-pointer hover:text-[#73726E]" onClick={() => window.open("/agreement/user")}>
          用户协议
        </span>
        <span className="cursor-pointer hover:text-[#73726E]" onClick={() => window.open("/agreement/privacy")}>
          隐私协议
        </span>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer" className="hover:text-[#73726E]">
          粤ICP备2025403006号-1
        </a>
      </div>
    </div>
  );
};

export default SidebarFoot;
